import { useContext, useEffect, useMemo, useState } from 'react';
import { useCurrentUser } from '@lp/core/hooks/useCurrentUser';
import { AuthContext } from '@lp/core/auth';
import { CaseDetailsPermissions } from '@/permissions';
import { Case, DraftCase } from '@/api/apis/case';
import { EmployeeRole } from '@/api/apis/resources';

const roleWithLimitedPermission = [
  EmployeeRole.RetrievalSurgeon,
  EmployeeRole.ClinicalSpecialist,
  EmployeeRole.ContractorRetrievalSurgeon,
];

// roles with permission to read any case
const rolesWithHigherPermission = [
  EmployeeRole.SuperAdmin,
  EmployeeRole.TechSupportAdmin,
  EmployeeRole.Admin,
  EmployeeRole.CLevel,
  EmployeeRole.QuoteApprovers,
  EmployeeRole.SrLogisticsManager,
  EmployeeRole.LogisticsManager,
  EmployeeRole.OrganLeadership,
  EmployeeRole.OrganManagement,
  EmployeeRole.OrganManagementSpecialist,
  EmployeeRole.ClinicalLead,
  EmployeeRole.ClinicalCoordinator,
  EmployeeRole.ScheduleManagerCs,
  EmployeeRole.ScheduleManagerRs,
  EmployeeRole.ScheduleManagerCc,
  EmployeeRole.MaintenanceOfficer,
  EmployeeRole.FinanceAuditor,
  EmployeeRole.RegionalCSManagement,
  EmployeeRole.NationalCSManagement,
];
/**
  NOTE: this hook is a temp solution to forbid some roles to view case details they did not assigned to.
  TODO remove once TMDXLP-6193 will be fully implemented
*/
export const useCaseDetailsViewPermission = (data: Case | DraftCase | undefined) => {
  const { data: user } = useCurrentUser();
  const { checkOwnCasePermission } = useContext(AuthContext);
  const [viewForbidden, setViewForbidden] = useState(false);
  const userRoles = user?.employee.roles || [];

  const hasHigherLevelRole = rolesWithHigherPermission.some((role) => userRoles.includes(role));
  const hasLimitedPermissionRole = roleWithLimitedPermission.some((role) => userRoles.includes(role));
  const hasOwnCasePermission = useMemo(
    () => data && checkOwnCasePermission(CaseDetailsPermissions.CaseRead, data as Case),
    [checkOwnCasePermission, data],
  );

  useEffect(() => {
    const isForbidden = Boolean(data && hasLimitedPermissionRole && !hasHigherLevelRole && !hasOwnCasePermission);

    setViewForbidden(isForbidden);
  }, [data, hasLimitedPermissionRole, hasHigherLevelRole, hasOwnCasePermission]);

  return viewForbidden;
};
